import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { DriverStatus } from '../../models/driver'
import { type DriverMvrState, isSelectable, pulledLabel } from '../../models/mvr'
import { MvrDriverStateCell } from './MvrDriverStateCell'

interface MvrDriverPickerProps {
  states: DriverMvrState[]
  selectedIds: Set<string>
  onToggle: (driverId: string) => void
  onToggleAll: () => void
}

export function MvrDriverPicker({ states, selectedIds, onToggle, onToggleAll }: MvrDriverPickerProps) {
  const selectable = states.filter(isSelectable)
  const allSelected = selectable.length > 0 && selectable.every((s) => selectedIds.has(s.driverId))

  return (
    <div className="max-h-[50vh] overflow-y-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10">
              <Checkbox
                checked={allSelected}
                onCheckedChange={onToggleAll}
                disabled={selectable.length === 0}
                aria-label="Select all drivers"
              />
            </TableHead>
            <TableHead>Driver</TableHead>
            <TableHead>Licence</TableHead>
            <TableHead>Last pulled</TableHead>
            <TableHead>MVR</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {states.map((state) => {
            const selectable = isSelectable(state)
            return (
              <TableRow key={state.driverId} className={selectable ? undefined : 'opacity-60'}>
                <TableCell>
                  <Checkbox
                    checked={selectedIds.has(state.driverId)}
                    onCheckedChange={() => onToggle(state.driverId)}
                    disabled={!selectable}
                    aria-label={`Select ${state.name}`}
                  />
                </TableCell>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    {state.name}
                    {state.driverStatus === DriverStatus.PENDING_ADD && (
                      <Badge variant="outline">Awaiting approval</Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {state.licenseState} {state.licenseNumber}
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">{pulledLabel(state)}</TableCell>
                <TableCell>
                  <MvrDriverStateCell state={state} />
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
